import type { CatalogItem, ProductDetail } from '@shared/index';

/**
 * Onaylanan Hub kataloglarının yerel görsel ve anlatı kaynağı.
 *
 * Kode oyunlarında onaylanan yerel sanat yönetimi kazanır. Böylece üretimdeki
 * eski/yer tutucu medya kayıtları yeni vitrini yeniden bozmaz. Bu tabloda
 * bulunmayan ürünlerde sunucudan gelen medya normal biçimde kullanılır.
 */
export interface GamePresentation {
  cover: string;
  banner: string;
  short: string;
  description: string;
  highlights: string[];
}

const ROOT = '/media/games-v2';
const IMMERSIVE_ROOT = '/media/immersive-v2';
const VR_ROOT = '/media/vr-v2';
const AR_ROOT = '/media/ar-v2';

export const GAME_PRESENTATION: Record<string, GamePresentation> = {
  akvaryum: {
    cover: `${ROOT}/akvaryum-cover.webp`,
    banner: `${ROOT}/akvaryum-banner.webp`,
    short: 'Balığını büyüt, akıntıyı oku ve renkli su altı dünyasında alanını genişlet.',
    description:
      'Akvaryum, oyuncuların telefonlarıyla yön verdiği, küçük balıklarla başlayıp doğru hedefleri yiyerek büyüdüğü kamusal ekran deneyimidir. Farklı boyuttaki balıklar aynı akvaryumda dolaşır; okunaklı siluetler ve sakin derinlik hissi kalabalık LED ekranlarda oyunu izlemeyi kolaylaştırır.',
    highlights: ['Telefonla anında katılım', 'Yeme ve büyüme döngüsü', 'Kalabalık ekranlara uygun okunaklı dünya'],
  },
  'carpisan-araba': {
    cover: `${ROOT}/carpisan-araba-cover.webp`,
    banner: `${ROOT}/carpisan-araba-banner.webp`,
    short: 'Kapibara pilotlarla arenaya gir, güçlendirmeleri topla ve rakiplerini alanın dışına it.',
    description:
      'Çarpışan Araba, kısa turlara göre tasarlanmış, neşeli ve rekabetçi bir arena oyunudur. Oyuncular küçük çarpışan arabaları telefonlarından yönetir; kalkan, hız ve puan güçlendirmeleri tur boyunca kararları değiştirir. Çarpışma dili şiddet yerine lunapark eğlencesi ve yumuşak görsel geri bildirim üzerine kuruludur.',
    highlights: ['12 oyuncuya kadar canlı katılım', 'Kalkan ve hız güçlendirmeleri', 'Aile dostu arena rekabeti'],
  },
  'alan-kapmaca': {
    cover: `${ROOT}/alan-kapmaca-cover.webp`,
    banner: `${ROOT}/alan-kapmaca-banner.webp`,
    short: 'Hareket eden güvenli alanda kal, rakiplerini dışarı it ve her saniyeyi puana çevir.',
    description:
      'Alan Kapmaca, tek bir ışıklı bölgenin çevresinde gelişen, ilk bakışta anlaşılır bir denge ve alan kontrolü oyunudur. Güvenli bölge tur boyunca yer değiştirip daralır; oyuncular içeride kalmak ve rakiplerini dışarı yönlendirmek için aynı anda hareket eder.',
    highlights: ['12 oyuncuya kadar canlı katılım', 'Hareket eden oyun alanı', 'Tek bakışta anlaşılır hedef'],
  },
  sumo: {
    cover: `${ROOT}/sumo-cover.webp`,
    banner: `${ROOT}/sumo-banner.webp`,
    short: 'Dairesel ringde konumunu koru, itiş yönünü seç ve son ana kadar içeride kal.',
    description:
      'Sumo, dairesel bir ringde konum ve itiş kararlarını öne çıkaran fizik tabanlı parti oyunudur. Kapibara karakterlerin yumuşak ve neşeli görsel dili rekabeti sertleştirmeden okunaklı kılar; oyuncunun amacı rakiplerini sınırın dışına iterken kendi dengesini korumaktır.',
    highlights: ['12 oyuncuya kadar canlı katılım', 'Konum ve yön odaklı oynanış', 'Neşeli fizik tepkileri'],
  },
  ebelemece: {
    cover: `${ROOT}/ebelemece-cover.webp`,
    banner: `${ROOT}/ebelemece-banner.webp`,
    short: 'Ebeden kaç, güçlendirmeleri kullan; yakalanırsan rol değiştir ve oyunda kal.',
    description:
      'Ebelemece, eleme kullanmadan sürekli rol değişimi yaratan hızlı bir kovalamaca oyunudur. Turuncu halkalı ebeler kaçanlara dokunduğunda roller değişir; yakalanan oyuncu oyundan çıkmaz. Sandıklar, kalkan ve hız güçlendirmeleri her turun rotasını farklılaştırır.',
    highlights: ['Elemesiz rol değişimi', 'Dört yönlü kolay kontrol', 'Güçlendirmelerle değişen rotalar'],
  },
  'kaybolan-zemin': {
    cover: `${ROOT}/kaybolan-zemin-cover.webp`,
    banner: `${ROOT}/kaybolan-zemin-banner.webp`,
    short: 'Turuncu işaretleri takip et, kaybolan karolardan kaç ve suyun üstünde kal.',
    description:
      'Kaybolan Zemin, su üzerindeki karo ızgarasında geniş tepki süresi ve açık tehlike işaretleri sunan bir hayatta kalma oyunudur. Çökecek karolar önceden turuncu X ile belirtilir; düşen oyuncu kısa süre sonra yeniden oyuna döner. Böylece her katılımcı tur sonuna kadar deneyimin parçası kalır.',
    highlights: ['12 oyuncuya kadar canlı katılım', 'Önceden görünen tehlikeler', 'Gecikmeye dayanıklı karo hareketi'],
  },
  atmosfer: {
    cover: `${ROOT}/atmosfer-cover.webp`,
    banner: `${ROOT}/atmosfer-banner.webp`,
    short: 'Hamleni planla, meyve atışının yayını kur ve değişen arazide aynı anda çözülmesini izle.',
    description:
      'Atmosfer, klasik sıra tabanlı arazi oyunlarını kalabalık kamusal ekranlara uyarlayan eşzamanlı planlama deneyimidir. Her oyuncu hareketini ve meyve atışını aynı anda hazırlar; ardından tüm kararlar birlikte çözülür. Ateş, silah ve patlama yerine meyveler, yumuşak arazi geçişleri ve suya düşüp geri dönme kullanılır.',
    highlights: ['Eşzamanlı hamle planlama', 'Meyve tabanlı atış sistemi', 'Değişen ve yeniden şekillenen arazi'],
  },
  quiz: {
    cover: `${ROOT}/quiz-cover.webp`,
    banner: `${ROOT}/quiz-banner.webp`,
    short: 'Soruyu ekrandan oku, cevabını telefondan seç ve hızını doğru yanıtla birleştir.',
    description:
      'Quiz, Türkçe soru ve dört büyük cevap alanıyla kamusal LED ekranlarda okunabilirlik için tasarlanmış canlı bilgi yarışmasıdır. Renkler şekillerle birlikte kullanılır; telefon ekranı da soru ve seçenekleri gösterir. Hız puanı etkiler ancak doğru cevap veren yavaş oyuncuyu değersizleştirmez.',
    highlights: ['Telefon ve büyük ekran birlikte', 'Renk ve şekille erişilebilir seçenekler', 'Yerel içeriklere uygun soru yapısı'],
  },
  'motion-stage': {
    cover: `${IMMERSIVE_ROOT}/motion-stage.webp`, banner: `${IMMERSIVE_ROOT}/motion-stage.webp`,
    short: 'Motion Track ile beden hareketini gerçek zamanlı sahne görsellerinin bir parçasına dönüştürür.',
    description: 'Motion Stage, performansçının hareketini izleyerek parçacık, ışık ve yüzey davranışlarını gerçek zamanlı değiştirir. CORTEX gerekli engine modüllerini arka planda hazırlar; sanatçı ve operatör yalnız performans içeriğiyle çalışır.',
    highlights: ['Gerçek zamanlı hareket takibi', 'Sahne ve çoklu projeksiyon uyumu', 'CORTEX ile yönetilen engine modülleri'],
  },
  'hareketin-yankisi': {
    cover: `${IMMERSIVE_ROOT}/hareketin-yankisi.webp`, banner: `${IMMERSIVE_ROOT}/hareketin-yankisi.webp`,
    short: 'Ziyaretçi jestlerini mekânda çoğalan ışık yankılarına dönüştüren etkileşimli enstalasyon.',
    description: 'Hareketin Yankısı, bedenin yön ve hız bilgisini katmanlı görsel izlere çevirir. Birden fazla ziyaretçi aynı yüzeyde farklı izler bırakabilir; sonuç mekân boyunca değişen ortak bir kompozisyondur.',
    highlights: ['Çoklu ziyaretçi etkileşimi', 'Jest ve hız duyarlı görseller', 'Mekâna göre uyarlanabilir kompozisyon'],
  },
  'yasayan-tuval': {
    cover: `${IMMERSIVE_ROOT}/yasayan-tuval.webp`, banner: `${IMMERSIVE_ROOT}/yasayan-tuval.webp`,
    short: 'Yaklaşma, durma ve yön değiştirme hareketleriyle gelişen üretken dijital duvar.',
    description: 'Yaşayan Tuval, ziyaretçilerin konumuna göre filizlenen, ayrışan ve yeniden birleşen biçimlerden oluşur. Müze, fuaye ve marka alanlarında sakin bir keşif akışı sunar.',
    highlights: ['Üretken görsel davranış', 'Temassız ziyaretçi etkileşimi', 'Küratöryel temaya uyarlanabilir içerik'],
  },
  'gelgit-odasi': {
    cover: `${IMMERSIVE_ROOT}/gelgit-odasi.webp`, banner: `${IMMERSIVE_ROOT}/gelgit-odasi.webp`,
    short: 'Adımların yön verdiği akıntılar ve ışık kırılmalarıyla çevreleyen su temalı deneyim.',
    description: 'Gelgit Odası, zemin ve duvar projeksiyonlarını tek bir akış alanı gibi çalıştırır. Ziyaretçilerin hareketi akıntı yönünü, parçacık yoğunluğunu ve yansıma davranışını değiştirir.',
    highlights: ['Zemin ve duvar bütünlüğü', 'Adım yönüne duyarlı akış', 'Sergi ve performans modları'],
  },
  'isik-bahcesi': {
    cover: `${IMMERSIVE_ROOT}/isik-bahcesi.webp`, banner: `${IMMERSIVE_ROOT}/isik-bahcesi.webp`,
    short: 'Ziyaretçi varlığına tepki vererek büyüyen biyolüminesan dijital flora.',
    description: 'Işık Bahçesi, mekânı hareketle gelişen bir ekosisteme dönüştürür. Yaklaşan ziyaretçiler yeni ışık gövdeleri oluşturur; birlikte hareket eden gruplar arasında görsel bağlar kurulur.',
    highlights: ['Bireysel ve grup tepkileri', 'Sürekli değişen dijital ekosistem', 'Sessiz sergi akışına uygun'],
  },
  'isik-orkestrasi': {
    cover: `${IMMERSIVE_ROOT}/isik-orkestrasi.webp`, banner: `${IMMERSIVE_ROOT}/isik-orkestrasi.webp`,
    short: 'El ve kol hareketlerini ışık, ritim ve mekânsal görsel katmanlara dönüştüren sahne eseri.',
    description: 'Işık Orkestrası, performansçının jestlerini tempo, yoğunluk ve yön parametrelerine bağlar. Müzik ile gerçek zamanlı görseller aynı sahne akışında buluşur.',
    highlights: ['Jest ile görsel yönetim', 'Ses ve ışık eşleşmesi', 'Canlı performans profilleri'],
  },
  'iz-koridoru': {
    cover: `${IMMERSIVE_ROOT}/iz-koridoru.webp`, banner: `${IMMERSIVE_ROOT}/iz-koridoru.webp`,
    short: 'Geçişleri ışıklı hareket izleri olarak saklayan mekâna özgü dijital koridor.',
    description: 'İz Koridoru, ziyaretçilerin yürüyüş çizgilerini kısa süre yaşayan görsel katmanlara dönüştürür. Yeni geçişler önceki izlerle karşılaşır ve zamanla silinir.',
    highlights: ['Mekâna özgü kalibrasyon', 'Zamanla silinen hareket hafızası', 'Kesintisiz ziyaretçi akışı'],
  },
  'kozmik-akis': {
    cover: `${IMMERSIVE_ROOT}/kozmik-akis.webp`, banner: `${IMMERSIVE_ROOT}/kozmik-akis.webp`,
    short: 'Bedenlerin çevresinde bükülen parçacık alanlarıyla anıtsal bir kozmik mekân kurar.',
    description: 'Kozmik Akış, geniş projeksiyon yüzeylerini yavaş hareket eden bir parçacık evrenine dönüştürür. Ziyaretçiler yaklaştıkça akış ayrılır, iz bırakır ve yeniden birleşir.',
    highlights: ['Geniş yüzey ve kubbe uyumu', 'Ziyaretçi çevresinde parçacık akışı', 'Anıtsal mekân deneyimi'],
  },
  'vr-arena': {
    cover: `${VR_ROOT}/vr-arena.webp`, banner: `${VR_ROOT}/vr-arena.webp`,
    short: 'Takım koordinasyonunu ve fiziksel alan farkındalığını öne çıkaran çok oyunculu VR arena deneyimi.',
    description: 'VR Arena, işletme tipi istasyonlarda yan yana çalışan oyuncuları ortak hedefler ve okunaklı takım bölgeleri etrafında buluşturur. CORTEX kurulumu ve güncellemeleri hazırlar.',
    highlights: ['Takım odaklı çok oyunculu akış', 'İşletme tipi VR istasyonları', 'CORTEX üzerinden tek noktadan kurulum'],
  },
  'derin-deniz-kesfi': {
    cover: `${VR_ROOT}/derin-deniz-kesfi.webp`, banner: `${VR_ROOT}/derin-deniz-kesfi.webp`,
    short: 'Biyolüminesan canlıları ve batık araştırma alanlarını keşfeden sakin tempolu VR macerası.',
    description: 'Derin Deniz Keşfi, oyuncuyu ışığın azaldığı okyanus katmanlarında yön bulmaya, çevresel ipuçlarını okumaya ve araştırma görevlerini tamamlamaya davet eder.',
    highlights: ['Sakin tempolu keşif', 'Çevresel anlatım ve araştırma', 'İlk kez VR kullananlara uygun akış'],
  },
  'yorunge-ekibi': {
    cover: `${VR_ROOT}/yorunge-ekibi.webp`, banner: `${VR_ROOT}/yorunge-ekibi.webp`,
    short: 'Bir uzay istasyonunun dış yüzeyinde koordinasyon ve problem çözme odaklı ortak görev.',
    description: 'Yörünge Ekibi, oyuncuların araçları paylaşarak arızalı istasyon parçalarını doğru sırayla onardığı iş birliği deneyimidir. Başarı iletişim ve görev paylaşımına dayanır.',
    highlights: ['İş birliğine dayalı görevler', 'Mekânsal problem çözme', 'Okunaklı görev yönlendirmesi'],
  },
  'antik-kent-yolculugu': {
    cover: `${VR_ROOT}/antik-kent-yolculugu.webp`, banner: `${VR_ROOT}/antik-kent-yolculugu.webp`,
    short: 'Canlandırılmış bir antik kentin gündelik yaşamını mekânsal anlatımla keşfedin.',
    description: 'Antik Kent Yolculuğu, ziyaretçiyi tarihsel araştırma diline sadık bir kent dokusuna taşır. Meydan, atölye ve kamusal yapılar çevresel anlatılarla keşfedilir.',
    highlights: ['Mekânsal tarih anlatımı', 'Rehberli ve serbest keşif', 'Kültür ve eğitim alanlarına uygun'],
  },
  'ritim-atolyesi': {
    cover: `${VR_ROOT}/ritim-atolyesi.webp`, banner: `${VR_ROOT}/ritim-atolyesi.webp`,
    short: 'Ses, hareket ve üç boyutlu enstrümanları bir araya getiren yaratıcı ritim deneyimi.',
    description: 'Ritim Atölyesi, oyuncunun sanal araçlarla farklı yüzeyleri çalarak katmanlı bir kompozisyon kurmasını sağlar. Tempo ve serbest üretim modları farklı akışlara uyarlanabilir.',
    highlights: ['Mekânsal ritim araçları', 'Serbest üretim ve görev modları', 'Hareket ile sesin doğrudan eşleşmesi'],
  },
  'kurtarma-protokolu': {
    cover: `${VR_ROOT}/kurtarma-protokolu.webp`, banner: `${VR_ROOT}/kurtarma-protokolu.webp`,
    short: 'Görüşün azaldığı bir araştırma tesisinde güvenli rota ve ekip iletişimi kurun.',
    description: 'Kurtarma Protokolü, oyuncuları yön bulma, enerji hatlarını yeniden devreye alma ve ekibi güvenli bölgeye ulaştırma görevinde birleştirir. Şiddet yerine baskı altında karar verme öne çıkar.',
    highlights: ['Şiddetsiz ekip senaryosu', 'Baskı altında karar verme', 'Ortak rota ve görev paylaşımı'],
  },
  'minik-dunyalar': {
    cover: `${VR_ROOT}/minik-dunyalar.webp`, banner: `${VR_ROOT}/minik-dunyalar.webp`,
    short: 'Etkinlik ve kültür alanlarında ziyaretçilerin sırayla keşfettiği erişilebilir mekânsal bulmaca deneyimi.',
    description: 'Minik Dünyalar, fuar, müze ve deneyim merkezlerindeki VR istasyonları için tasarlanmış kısa döngülü bir mekânsal bulmaca deneyimidir. Ziyaretçi yüzen adaları farklı açılardan inceleyerek köprüleri, su yollarını ve mekanik düzenekleri tamamlar.',
    highlights: ['İlk kez VR kullananlara uygun', 'Kısa etkinlik seansları', 'Minyatür dünyalarda mekânsal keşif'],
  },
  'sanal-sanat-atolyesi': {
    cover: `${VR_ROOT}/sanal-sanat-atolyesi.webp`, banner: `${VR_ROOT}/sanal-sanat-atolyesi.webp`,
    short: 'Işık, hacim ve hareketle üç boyutlu işler üretebileceğiniz yaratıcı VR stüdyosu.',
    description: 'Sanal Sanat Atölyesi, oyuncuya havada çizim, hacim şekillendirme ve malzeme deneme araçları sunar. Üretilen kompozisyonlar farklı açılardan gezilebilir.',
    highlights: ['Üç boyutlu çizim ve şekillendirme', 'Serbest üretim akışı', 'Atölye ve etkinlik kullanımına uygun'],
  },
  'dinozor-parki-ar': {
    cover: `${AR_ROOT}/dinozor-parki-ar.webp`, banner: `${AR_ROOT}/dinozor-parki-ar.webp`,
    short: 'Şehir parkını ölçekli dinozorlar ve keşif duraklarıyla açık hava AR rotasına dönüştürür.',
    description: 'Dinozor Parkı AR, belediye parkları, rekreasyon alanları ve tematik etkinlikler için mekâna bağlı bir keşif projesidir. Ziyaretçiler işaretli rotada farklı türleri gözlemler; kurum içerik, durak ve yönlendirme dilini kendi alanına göre uyarlayabilir.',
    highlights: ['Mekâna bağlı açık hava rotası', 'Kurum içeriğine göre düzenlenebilir duraklar', 'Kalabalık ziyaretçi akışına uygun'],
  },
  'yasayan-hayvanat-bahcesi': {
    cover: `${AR_ROOT}/yasayan-hayvanat-bahcesi.webp`, banner: `${AR_ROOT}/yasayan-hayvanat-bahcesi.webp`,
    short: 'Kamusal alanlara doğal ölçekte dijital hayvanlar ve etkileşimli öğrenme istasyonları yerleştirir.',
    description: 'Yaşayan Hayvanat Bahçesi; park, müze bahçesi ve festival alanlarında gerçek hayvan barındırmadan yönlendirmeli bir AR gözlem rotası kurar. Tür kartları, anlatıcı içerikleri ve görevler kurumun ziyaretçi profiline göre yapılandırılır.',
    highlights: ['Doğal ölçekte AR hayvanlar', 'Park ve festival kurulumları', 'Küratöryel öğrenme durakları'],
  },
  'zaman-kapisi-tarih-parki': {
    cover: `${AR_ROOT}/zaman-kapisi-tarih-parki.webp`, banner: `${AR_ROOT}/zaman-kapisi-tarih-parki.webp`,
    short: 'Tarihî yapıları, meydanları ve arkeolojik alanları dönem sahneleriyle yeniden görünür kılar.',
    description: 'Zaman Kapısı, kültür rotaları ve ören yerleri için konuma bağlı canlandırmalar sunar. Ziyaretçi belirli bakış noktalarında yapının geçmiş hâlini, gündelik yaşam sahnelerini ve küratöryel açıklamaları birlikte görür.',
    highlights: ['Konuma bağlı tarih canlandırması', 'Kültür rotalarına uygun', 'Rehberli ve serbest keşif'],
  },
  'ar-takim-arenasi': {
    cover: `${AR_ROOT}/ar-takim-arenasi.webp`, banner: `${AR_ROOT}/ar-takim-arenasi.webp`,
    short: 'Fuar ve etkinlik alanlarında takımların aynı fiziksel sahada birlikte oynadığı çok oyunculu AR oyunu.',
    description: 'AR Takım Arenası, katılımcıların ortak hedefleri bulduğu, alan tuttuğu ve takım görevlerini tamamladığı yerel çok oyunculu bir etkinlik formatıdır. Oyun sınırları, tur süresi ve marka katmanları kurulum alanına göre hazırlanır.',
    highlights: ['Yerel çok oyunculu oyun', 'Fuar ve etkinlik kurulumu', 'Alana göre uyarlanabilir görevler'],
  },
  'fuar-gorevi-ar': {
    cover: `${AR_ROOT}/fuar-gorevi-ar.webp`, banner: `${AR_ROOT}/fuar-gorevi-ar.webp`,
    short: 'Ziyaretçileri stantlar ve deneyim noktaları arasında yönlendiren görev tabanlı AR etkinliği.',
    description: 'Fuar Görevi AR, büyük etkinlik alanlarında keşfi bir oyun akışına dönüştürür. Katılımcılar dijital ipuçlarını takip eder, seçili stantlarda görevleri tamamlar ve etkinlik kurgusuna bağlı koleksiyonları açar.',
    highlights: ['Stantlar arası ziyaretçi akışı', 'Görev ve koleksiyon sistemi', 'Etkinliğe özel içerik'],
  },
  'sehir-efsaneleri-ar': {
    cover: `${AR_ROOT}/sehir-efsaneleri-ar.webp`, banner: `${AR_ROOT}/sehir-efsaneleri-ar.webp`,
    short: 'Kent belleğini hikâye durakları, karakterler ve mekâna bağlı dijital sahnelerle keşfe açar.',
    description: 'Şehir Efsaneleri; belediyeler, turizm birimleri ve kültür festivalleri için yürünebilir bir anlatı rotasıdır. Her durak bulunduğu sokağa veya yapıya bağlı özgün bir AR sahnesi açar.',
    highlights: ['Yürünebilir kent rotası', 'Mekâna özgü dijital sahneler', 'Turizm ve kültür etkinliklerine uygun'],
  },
  'eko-kesif-ar': {
    cover: `${AR_ROOT}/eko-kesif-ar.webp`, banner: `${AR_ROOT}/eko-kesif-ar.webp`,
    short: 'Doğa parkları ve çevre etkinlikleri için türleri, habitatları ve ekolojik ilişkileri görünür kılar.',
    description: 'Eko Keşif AR; sulak alan, botanik parkı ve çevre festivallerinde ziyaretçiyi sahadaki gözlem noktalarına yönlendirir. Dijital katmanlar mevsim, habitat ve kurumun eğitim içeriğine göre değiştirilebilir.',
    highlights: ['Doğa alanına bağlı keşif', 'Mevsimsel içerik katmanları', 'Çevre etkinliklerine uygun'],
  },
  'afet-hazirlik-ar': {
    cover: `${AR_ROOT}/afet-hazirlik-ar.webp`, banner: `${AR_ROOT}/afet-hazirlik-ar.webp`,
    short: 'Kurumların eğitim alanlarında güvenli kararları ve tahliye adımlarını senaryo içinde çalıştırır.',
    description: 'Afet Hazırlık AR, belediye ve kurum eğitim günlerinde fiziksel tatbikatı dijital yönlendirmelerle destekleyen proje formatıdır. Katılımcılar risk işaretlerini tanır, güvenli rotayı izler ve ekip görevlerini kontrollü bir alanda uygular.',
    highlights: ['Kurum eğitim alanlarına uygun', 'Senaryo tabanlı yönlendirme', 'Kontrollü grup uygulaması'],
  },
};

export function presentationFor(slug: string): GamePresentation | undefined {
  return GAME_PRESENTATION[slug];
}

export function catalogCover(item: Pick<CatalogItem, 'slug' | 'coverUrl'>): string | null {
  return presentationFor(item.slug)?.cover || item.coverUrl || null;
}

export function catalogBanner(item: Pick<CatalogItem, 'slug' | 'bannerUrl' | 'coverUrl'>): string | null {
  return presentationFor(item.slug)?.banner || item.bannerUrl || item.coverUrl || null;
}

export function catalogThumb(item: Pick<CatalogItem, 'slug' | 'thumbUrl' | 'coverUrl'>): string | null {
  return presentationFor(item.slug)?.cover || item.thumbUrl || item.coverUrl || null;
}

export function productCopy(product: ProductDetail, language: 'tr' | 'en' = 'tr'): {
  summary: string;
  description: string;
  highlights: string[];
} {
  const fallback = presentationFor(product.slug);
  if (language === 'en') {
    return {
      summary: product.summary.trim(),
      description: product.description.trim(),
      // Eski statik vurgu maddeleri yalnız Türkçe yazılmıştı. İngilizce
      // yüzeyde Türkçe metin sızdırmak yerine bu opsiyonel satır çizilmez.
      highlights: [],
    };
  }
  return {
    summary: fallback?.short || product.summary.trim() || '',
    description: fallback?.description || product.description.trim() || '',
    highlights: fallback?.highlights ?? [],
  };
}
